import { useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import { useFormatters } from '../../lib/format'
import { SECTION_ICONS } from '../../lib/section-icons'
import { AssetPicker } from '../AssetPicker'
import { PageHeader } from '../shared/PageHeader'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { EmptyState } from '../ui/empty-state'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Skeleton } from '../ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import type { ListingStatusFilter } from '../../routes/$org/listings/index'
import { ListingCreateDialog } from './ListingCreateDialog'
import { ListingStatusBadge } from './listingShared'

const ALL = '__all__'
const STATUSES: ListingStatusFilter[] = ['draft', 'published', 'paused', 'let', 'withdrawn']

/** The rental listings of the organization (spec 2026-10-25-10). */
export function ListingsPage() {
    const { org } = useParams({ from: '/$org/listings/' })
    const search = useSearch({ from: '/$org/listings/' })
    const navigate = useNavigate()
    const { t } = useTranslation('listings')
    const { formatDate, formatMoney } = useFormatters()
    const canWrite = useCan('listings.write')
    const [creating, setCreating] = useState(false)
    useBreadcrumbs([{ title: t('common:nav.home'), path: `/${org}` }, { title: t('title') }])

    const setSearch = (patch: Partial<typeof search>) =>
        navigate({ to: '/$org/listings', params: { org }, search: { ...search, ...patch }, replace: true })

    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/listings', {
        params: { path: { orgCode: org }, query: { status: search.status, asset_uid: search.asset } },
    })
    const rows = data?.listings ?? []

    return (
        <div className="space-y-6" data-testid="listings-page">
            <PageHeader
                icon={SECTION_ICONS.listings}
                title={t('title')}
                description={t('description')}
                actions={canWrite ? (
                    <Button onClick={() => setCreating(true)} data-testid="listing-create-button">
                        {t('create.button')}
                    </Button>
                ) : undefined}
            />

            <div className="grid gap-3 md:grid-cols-2" data-testid="listings-filters">
                <div>
                    <Label className="mb-1 block text-sm">{t('filter.status')}</Label>
                    <Select value={search.status ?? ALL} onValueChange={(v) => setSearch({ status: v === ALL ? undefined : (v as ListingStatusFilter) })}>
                        <SelectTrigger data-testid="listings-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL} data-testid="listings-status-filter-all">{t('filter.status_all')}</SelectItem>
                            {STATUSES.map((s) => (
                                <SelectItem key={s} value={s} data-testid={`listings-status-filter-${s}`}>{t(`status.${s}`)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="mb-1 block text-sm">{t('filter.asset')}</Label>
                    <AssetPicker org={org} value={search.asset} testId="listings-asset-filter" onChange={(uid) => setSearch({ asset: uid })} />
                </div>
            </div>
            {(search.status || search.asset) && (
                <Button variant="ghost" size="sm" onClick={() => setSearch({ status: undefined, asset: undefined })} data-testid="listings-filter-clear">
                    {t('filter.clear')}
                </Button>
            )}

            {isLoading ? (
                <Skeleton className="h-40 w-full" data-testid="listings-loading" />
            ) : rows.length === 0 ? (
                <EmptyState icon={SECTION_ICONS.listings} title={t('empty.title')} description={t('empty.description')} data-testid="listings-empty" />
            ) : (
                <Card>
                    <CardContent className="overflow-x-auto p-0">
                        <Table data-testid="listings-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('column.asset')}</TableHead>
                                    <TableHead>{t('column.status')}</TableHead>
                                    <TableHead className="text-right">{t('column.rent')}</TableHead>
                                    <TableHead>{t('column.available')}</TableHead>
                                    <TableHead>{t('column.commercial')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow
                                        key={row.uid}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() => navigate({ to: '/$org/listings/$listingUid', params: { org, listingUid: row.uid } })}
                                        data-testid="listing-row"
                                        data-listing-uid={row.uid}
                                    >
                                        <TableCell className="font-medium">
                                            <Link to="/$org/listings/$listingUid" params={{ org, listingUid: row.uid }} className="hover:underline" data-testid="listing-row-link">
                                                {row.asset_name}
                                            </Link>
                                        </TableCell>
                                        <TableCell><ListingStatusBadge status={row.status} testId="listing-row-status" /></TableCell>
                                        <TableCell className="text-right tabular-nums" data-testid="listing-row-rent">
                                            {row.asking_rent_minor == null ? '—' : formatMoney(row.asking_rent_minor, row.currency)}
                                        </TableCell>
                                        <TableCell data-testid="listing-row-available">{row.available_from ? formatDate(row.available_from) : '—'}</TableCell>
                                        <TableCell data-testid="listing-row-commercial">{t(`commercial.${row.commercial_status}`, { defaultValue: row.commercial_status })}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
            <ListingCreateDialog org={org} open={creating} onOpenChange={setCreating} />
        </div>
    )
}
